import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Descriptions, Input, Select, DatePicker, message, Statistic, Row, Col } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { get } from '../../utils/merchantApi';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const MerchantSettlements = () => {
  const [loading, setLoading] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_amount: 0, total_fee: 0 });
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [detailVisible, setDetailVisible] = useState(false);
  const [settlementDetail, setSettlementDetail] = useState(null);
  const [filters, setFilters] = useState({
    status: undefined,
    dateRange: null
  });

  useEffect(() => {
    fetchSettlements();
  }, [page]);

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.status) params.status = filters.status;
      if (filters.dateRange?.[0]) params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      if (filters.dateRange?.[1]) params.endDate = filters.dateRange[1].format('YYYY-MM-DD');

      const response = await get('/merchant/settlements', params);
      setSettlements(response.data.list || []);
      setPagination(response.data.pagination || { total: 0, total_amount: 0, total_fee: 0 });
    } catch (error) {
      console.error('获取结算记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchSettlements();
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await get(`/merchant/settlements/${record.trade_no}`);
      setSettlementDetail(response.data);
      setDetailVisible(true);
    } catch (error) {
      message.error('获取结算详情失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待结算' },
      processing: { color: 'blue', text: '处理中' },
      success: { color: 'green', text: '已结算' },
      paid: { color: 'green', text: '已结算' },
      failed: { color: 'red', text: '失败' },
      cancelled: { color: 'default', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '结算单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 180
    },
    {
      title: '订单金额',
      dataIndex: 'order_amount',
      key: 'order_amount',
      width: 120,
      render: (val, record) => `${record.currency_code || 'CNY'} ${Number(val || 0).toFixed(2)}`
    },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      width: 100,
      render: (val) => val ? `${Number(val).toFixed(2)}` : '-'
    },
    {
      title: '结算金额',
      dataIndex: 'settle_amount',
      key: 'settle_amount',
      width: 120,
      render: (val, record) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          {record.currency_code} {Number(val || 0).toFixed(2)}
        </span>
      )
    },
    // {
    //   title: '结算金额(CNY)',
    //   dataIndex: 'settle_amount_cny',
    //   key: 'settle_amount_cny',
    //   width: 120,
    //   render: (val) => val ? `¥${Number(val).toFixed(2)}` : '-'
    // },
    {
      title: '汇率',
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      width: 100,
      render: (val) => val ? Number(val).toFixed(8) : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
          详情
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic 
              title="结算总笔数" 
              value={pagination.total} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="结算总金额" 
              value={pagination.total_amount || 0} 
              precision={2}
              suffix=""
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="手续费合计" 
              value={pagination.total_fee || 0} 
              precision={2}
              suffix=""
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
        </Row>
        <Space wrap>
          <Select
            placeholder="结算状态"
            allowClear
            style={{ width: 120 }}
            value={filters.status}
            onChange={(val) => setFilters({ ...filters, status: val })}
          >
            <Option value="pending">待结算</Option>
            <Option value="processing">处理中</Option>
            <Option value="success">已结算</Option>
            <Option value="failed">失败</Option>
          </Select>
          <RangePicker 
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={settlements}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: limit,
            total: pagination.total,
            onChange: setPage,
            showSizeChanger: false
          }}
        />
      </Card>

      <Modal
        title="结算详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={600}
      >
        {settlementDetail && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="结算单号" span={2}>{settlementDetail.trade_no}</Descriptions.Item>
            <Descriptions.Item label="订单金额">{settlementDetail.currency_code} {Number(settlementDetail.order_amount || 0).toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="手续费率">{settlementDetail.fee_rate ? (Number(settlementDetail.fee_rate) * 100).toFixed(2) + '%' : '-'}</Descriptions.Item>
            <Descriptions.Item label="手续费">{Number(settlementDetail.fee_amount || 0).toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="结算金额">{settlementDetail.currency_code} {Number(settlementDetail.settle_amount || 0).toFixed(2)}</Descriptions.Item>
            {/* <Descriptions.Item label="结算金额(CNY)">¥{Number(settlementDetail.settle_amount_cny || 0).toFixed(2)}</Descriptions.Item> */}
            <Descriptions.Item label="汇率">{Number(settlementDetail.exchange_rate || 0).toFixed(8)}</Descriptions.Item>
            <Descriptions.Item label="状态">{getStatusTag(settlementDetail.status)}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{settlementDetail.created_at ? dayjs(settlementDetail.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="结算时间">{settlementDetail.settle_time ? dayjs(settlementDetail.settle_time).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>{settlementDetail.remark || '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantSettlements;
